import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	EventGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchEvents,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	createRemoteDataSource,
	useAlerts,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EventPreviewModel,
	EventDateSortDirection,
	EventDateFilterType,
	EventLocationType
} from "client/Api/Hub/Events/eventViewer";
import {
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	getPropertySortText,
	MultiSelectOption
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import {
	HubItemCategoryModel,
	listCategories
} from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { LocationType } from "client/Api/EventsManagement/eventsManagement";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { EventItemView } from "../Items/EventItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type EventSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const EventSearch = (props: EventSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [formData, setFormData] = useState<EventSearchFormData>({
		...defaultFilters,
		sort: defaultSortValue
	});
	const [categoryOptions, setCategoryOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);

	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<EventSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const userTimezone: TimeZone = useUserTimezone();

	const listHandle: RefObject<ScrollableVirtualListHandle<EventGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<EventGlobalSearchResultItem>>(null);

	const partialDataSourceOptions = useValueEqualMemo(
		(): Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				categoryIds: formData.categoryIds,
				dateFilterType: formData.dateFilterType,
				locationTypes: formData.locationTypes
			},
			sorting: formData.sort,
			globalContext: {
				searchVersion: searchTerm.version
			}
		}),
		[
			searchTerm,
			formData.categoryIds,
			formData.dateFilterType,
			formData.locationTypes,
			formData.sort
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsLoadingFilters(true);
					const categories: readonly HubItemCategoryModel[] = await listCategories(HubItemKind.Event, signal);

					setCategoryOptions(categories.map(convertCategoryToOption));
					setIsLoadingFilters(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoadingFilters(false);
					console.error(e);
				}

			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={130}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="event-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: EventGlobalSearchResultItem, index: number): ReactElement => (
						<EventItemView
							navigationIndex={index}
							item={item}
							itemScope={eventSearchScope}
							isMobile={isMobile}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
						isExpandedByDefault
					/>
					<SearchSettings.Select
						label={localize("_FilterBy")}
						attachable={formHandler.for("dateFilterType")}
						options={dateFilterTypeOptions}
					/>
					<SearchSettings.MultiSelect
						label={localize("_Category")}
						attachable={formHandler.for("categoryIds")}
						options={categoryOptions}
						placeholder={localize("_SelectCategory(ies)")}
						hasBusyIndicator={isLoadingFilters}
					/>
					<SearchSettings.MultiSelect
						label={localize("_EventType")}
						attachable={formHandler.for("locationTypes")}
						options={locationTypesOptions}
						placeholder={localize("_SelectEventType(s)")}
					/>
				</SearchSettings>
			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
EventSearch.displayName = "EventSearch";

export const PAGE_SIZE = 20;

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<EventGlobalSearchResultItem> & "event.startDate";
};

type EventSearchFormData = {
	dateFilterType: EventDateFilterType;
	categoryIds: readonly number[];
	locationTypes: readonly EventLocationType[];
	sort: SortValue;
};

type FilterModel = Omit<EventSearchFormData, "sort">;

const defaultFilters: FilterModel = {
	categoryIds: EMPTY_ARRAY,
	locationTypes: EMPTY_ARRAY,
	dateFilterType: EventDateFilterType.Upcoming
};

type FilterContext = {
	dateFilterType: EventDateFilterType;
	categoryIds: readonly number[];
	locationTypes: readonly EventLocationType[];
};

const useDataSource = createRemoteDataSource<
	EventGlobalSearchResultItem,
	FilterContext,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook({
		performRequest: async (options): Promise<LazyLoadPageResult<EventGlobalSearchResultItem>> => {
			if (isNilOrWhiteSpace(options.searchTerm)) {
				return {items: [], hasMore: false};
			}

			const afterEvent: EventPreviewModel | undefined = options.after
				?.event;

			return await searchEvents(
				{
					searchTerm: options.searchTerm,
					categoryIds: options.filterState.categoryIds,
					amount: options.amount,
					pagingInformation: isNotNil(afterEvent)
						? {
							isSet: true,
							value: {
								id: afterEvent.id,
								startDate: afterEvent.startDate
							}
						}
						: { isSet: false },
					sortDateDirection: convertSort(
						ensureNotNil(options.sorting)
					),
					dateFilterType: options.filterState.dateFilterType,
					locationTypes: options.filterState.locationTypes
				},
				options.signal
			);
		},
	});

function convertSort(
	sortingState: DataSourceSortableState<EventGlobalSearchResultItem>
): EventDateSortDirection {
	if (!sortingState) {
		return EventDateSortDirection.NearToFar;
	}

	if (sortingState.direction === DataSourceSortDirection.None) {
		throw new Error("sorting directions is not specified.");
	}

	switch(sortingState.propertyName) {
		case "event.startDate":
			break;
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}

	return sortingState.direction === DataSourceSortDirection.Ascending
		? EventDateSortDirection.NearToFar
		: EventDateSortDirection.FarToNear;
}

function getUniqueKey(item: EventGlobalSearchResultItem): string {
	return `${item.event.id}`;
}

const defaultSortValue: SortValue = {
	direction: DataSourceSortDirection.Ascending,
	propertyName: "event.startDate"
};

const sortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: getPropertySortText("_EventDate", "_NearFar"),
		value: defaultSortValue,
		valueTrackingId: "event-date-near-far"
	},
	{
		text: getPropertySortText("_EventDate", "_FarNear"),
		value: {
			direction: DataSourceSortDirection.Descending,
			propertyName: "event.startDate"
		},
		valueTrackingId: "event-date-far-near"
	}
];

const locationTypesOptions: readonly SelectOption<number>[] = [
	{
		text: localize("_Online"),
		value: LocationType.Online
	},
	{
		text: localize("_Physical"),
		value: LocationType.Physical
	},
	{
		text: localize("_Hybrid"),
		value: LocationType.Hybrid
	}
];

const dateFilterTypeOptions: readonly SelectOption<number>[] = [
	{
		text: localize("_UpcomingEvents"),
		value: EventDateFilterType.Upcoming
	},
	{
		text: localize("_PastEvents"),
		value: EventDateFilterType.Past
	}
];

function convertCategoryToOption(category: HubItemCategoryModel): SelectOption<number> {
	return {
		text: category.name,
		value: category.id
	};
}

const eventSearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Events
};